import { type RefObject, useCallback, useRef, useState } from "react";

const AT_BOTTOM_TOLERANCE_PX = 48;

export const useFollowLatest = (scroller: RefObject<HTMLElement>) => {
  const following = useRef(true);
  const [hasUnseenLatest, setHasUnseenLatest] = useState(false);

  const scrollTo = useCallback(
    (top: number) => {
      const element = scroller.current;
      if (!element) return;
      if (element.scrollTo) element.scrollTo({ top });
      else element.scrollTop = top;
    },
    [scroller],
  );
  const toBottom = useCallback(() => scrollTo(scroller.current?.scrollHeight ?? 0), [scrollTo, scroller]);

  const onScroll = useCallback(() => {
    const element = scroller.current;
    if (!element) return;
    const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight <= AT_BOTTOM_TOLERANCE_PX;
    following.current = atBottom;
    if (atBottom) setHasUnseenLatest(false);
  }, [scroller]);

  const contentChanged = useCallback(() => {
    if (following.current) toBottom();
    else setHasUnseenLatest(true);
  }, [toBottom]);

  const followAgain = useCallback(() => {
    following.current = true;
    setHasUnseenLatest(false);
    toBottom();
  }, [toBottom]);

  const startOver = useCallback(() => {
    following.current = true;
    setHasUnseenLatest(false);
    scrollTo(0);
  }, [scrollTo]);

  return { onScroll, contentChanged, followAgain, startOver, hasUnseenLatest };
};
